import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import type { BlisterProtocolMissing } from '../services/patientBlisterService';

/**
 * PRX-46 — every string the blister-pack surfaces render, in one place.
 *
 * Pure and string-only: no component, no hook and no service formats a blister
 * value itself, so the chip, the Patient 360 header cell and the settings
 * dialog can never drift apart.
 *
 * TWO RULES CARRY THE WEIGHT HERE:
 *
 * 1. NOTHING IS GUESSED. A missing pack size renders "size not set", never a
 *    default. PRX-50's pack-date calculator consumes the size, so a guessed
 *    "large" produces a wrong date on a real patient — the same shape as the
 *    Mississauga-default getPharmacyInfo() incident.
 *
 * 2. NO DATE IS EVER BUILT FROM A BARE 'YYYY-MM-DD'. `new Date('2026-09-21')`
 *    parses as UTC midnight and reads as 20 Sep in every western timezone, so
 *    every parse here goes through `parseISO(value + 'T00:00:00')` per the
 *    project date rules. `today` is injected by the caller
 *    (`getPharmacyTodayString()`), never read from the clock in here.
 *
 * `lastPackSentDate` IS ALWAYS NULL IN PRX-46. Nothing writes
 * `blister_last_pack_sent_at` until PRX-48 ships the pack advance, so the
 * "Last pack sent …" branch is exercised by the specs today and by real data
 * later. It is written now so PRX-48 changes no formatting code.
 */

/** The two cycles the settings dialog offers, in days. Anything else is a synced value. */
const CYCLE_WEEKS_LABELS: Record<number, string> = {
  14: 'every 2 weeks',
  28: 'every 4 weeks',
};

/** The two cycles the pharmacy offers, in days. Anything else came from Fillware. */
export const BLISTER_OFFERED_CYCLES: readonly number[] = [14, 28];

/**
 * The cycle choices for a picker: the offered ones, plus any value already in
 * play (the stored cycle, the current selection) that is neither. An odd
 * synced cycle — Fillware holds 7-day patients — is therefore always an
 * option, rendered verbatim, never silently rewritten to 14 or 28 by opening
 * a dialog. Shared by `BlisterSettingsDialog` and PRX-66's change dialog.
 */
export function blisterCycleOptions(...inPlay: Array<number | null | undefined>): number[] {
  const options = [...BLISTER_OFFERED_CYCLES];
  for (const value of inPlay) {
    if (typeof value === 'number' && !options.includes(value)) options.push(value);
  }
  return options.sort((a, b) => a - b);
}

/**
 * The cycle, in the unit it was set in.
 *
 * Fillware's live data holds DispillWeeks = 4 for 550 patients and 1 for 2, and
 * no 2-week value exists at all — so an odd synced cycle is rendered verbatim in
 * days rather than rounded to one of the offered options.
 *
 * Null cannot reach here for a flagged patient: the PATCH route refuses
 * `blisterPack: true` without a cycle, and a Fillware-synced patient always has
 * one (`resolveCycleDays` throws otherwise). The branch is defensive only.
 */
export function formatBlisterCycle(cycleDays: number | null): string {
  if (cycleDays === null || cycleDays === undefined) return 'cycle not set';
  return CYCLE_WEEKS_LABELS[cycleDays] ?? `every ${cycleDays} days`;
}

/** The size, or the plain admission that nobody has set one. Never guessed. */
export function formatBlisterPackSize(packSize: 'large' | 'small' | null): string {
  return packSize ?? 'size not set';
}

/**
 * "every 4 weeks" -> "Every 4 weeks": a value above that stands on its own as
 * a label or a menu item. The ONE copy — `PackScheduleCard`,
 * `PackagingAndRefillCard` and `BlisterSettingsDialog` each kept a private one
 * for the same strings, so the three surfaces could drift apart.
 */
export function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The chip label: "Blister pack · every 4 weeks · large". */
export function formatBlisterChipLabel(info: {
  cycleDays: number | null;
  packSize: 'large' | 'small' | null;
}): string {
  return [
    'Blister pack',
    formatBlisterCycle(info.cycleDays),
    formatBlisterPackSize(info.packSize),
  ].join(' · ');
}

/**
 * 'YYYY-MM-DD' -> '21 Sep 2026'. Never `new Date(dateStr)` — see rule 2 above.
 * Exported for PRX-66's change-answer copy, so every blister surface words a
 * day the same way.
 */
export function formatDayMonthYear(dateStr: string): string {
  return format(parseISO(`${dateStr}T00:00:00`), 'd MMM yyyy');
}

/** The same day rendering the header line uses, for a read-only field. */
export function formatBlisterDate(dateStr: string | null): string {
  return dateStr ? formatDayMonthYear(dateStr) : 'Not set';
}

/**
 * PRX-102: why the server could not compute a blister date. A missing input is
 * a clause in one "Some dates cannot be calculated" sentence; an anchor that
 * could not be read or is not a Monday is a sentence of its own, because it
 * asks for a different action.
 *
 * Typed on `BlisterProtocolMissing`, so a code added on the server without a
 * sentence here fails `tsc` instead of showing a raw key.
 */
const BLISTER_PROTOCOL_MISSING_TEXT: Record<BlisterProtocolMissing, { clause: string } | { sentence: string }> = {
  nextPackDate: { clause: 'there is no next pack date' },
  packSize: { clause: 'the pack size is not set' },
  frequency: { clause: 'the cycle is not 2, 4 or 12 weeks' },
  cycleAnchor: { clause: 'this pharmacy has no blister cycle start date' },
  cycleAnchorUnavailable: { sentence: 'Blister cycles could not be calculated. Try again.' },
  cycleAnchorInvalid: { sentence: "This pharmacy's blister cycle start date is not a Monday." },
};

/**
 * PRX-102: what is missing, in words, or null when every input was there.
 * Lets a "Not set" field say why instead of reading as a lost value.
 */
export function formatBlisterProtocolMissing(missing: readonly BlisterProtocolMissing[]): string | null {
  const clauses: string[] = [];
  const sentences: string[] = [];
  for (const key of missing) {
    const text = BLISTER_PROTOCOL_MISSING_TEXT[key] as
      (typeof BLISTER_PROTOCOL_MISSING_TEXT)[BlisterProtocolMissing] | undefined;
    // A newer server can send a code this build has no sentence for (the
    // frontend and backend deploy separately). Say so plainly, never throw.
    if (!text) {
      const generic = 'Some blister dates cannot be calculated.';
      if (!sentences.includes(generic)) sentences.push(generic);
      continue;
    }
    if ('clause' in text) clauses.push(text.clause);
    else sentences.push(text.sentence);
  }
  const parts = [
    ...(clauses.length > 0 ? [`Some dates cannot be calculated: ${clauses.join('; ')}.`] : []),
    ...sentences,
  ];
  return parts.length === 0 ? null : parts.join(' ');
}

/**
 * PRX-102: the note shown when a typed pack date is a Saturday or Sunday.
 *
 * The SERVER rolls it (`rollBackOffWeekend`, the one implementation) and
 * stores the Friday before. This only says so before the save, so the change
 * is never silent; it moves nothing itself. Null on a weekday or no date.
 */
export function blisterWeekendRollNote(dateStr: string | null): string | null {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const day = parseISO(`${dateStr}T00:00:00`);
  if (Number.isNaN(day.getTime())) return null;
  const dow = day.getDay();
  if (dow !== 0 && dow !== 6) return null;
  const friday = format(addDays(day, dow === 6 ? -1 : -2), 'yyyy-MM-dd');
  return `${formatDayMonthYear(dateStr)} is a ${dow === 6 ? 'Saturday' : 'Sunday'}. `
    + `Pack dates are weekdays only, so it will be saved as Friday ${formatDayMonthYear(friday)}.`;
}

/**
 * An INSTANT (`updatedAt`, an ISO timestamp) as the local calendar day it
 * happened on.
 *
 * Deliberately NOT `formatDayMonthYear`: this value carries a time of day, so
 * it goes through `Date` — taking the leading 'YYYY-MM-DD' off the string would
 * show the UTC day and read as tomorrow for anything saved after 8pm.
 */
export function formatBlisterUpdatedOn(updatedAt: string | null): string | null {
  if (!updatedAt) return null;
  const instant = new Date(updatedAt);
  return Number.isNaN(instant.getTime()) ? null : format(instant, 'd MMM yyyy');
}

/**
 * Who set these values, and what changing them costs.
 *
 * The Fillware wording names the consequence out loud: a manual edit takes the
 * patient out of the nightly sync for good, and staff have to know that before
 * they touch the fields, not after.
 */
export function formatBlisterSourceLine(info: {
  source: 'fillware' | 'manual' | null;
  updatedBy: string | null;
  updatedAt: string | null;
}): string | null {
  const on = formatBlisterUpdatedOn(info.updatedAt);

  if (info.source === 'fillware') {
    return on
      ? `Set from Fillware on ${on}. Changing anything here stops the nightly sync from updating this patient.`
      : 'Set from Fillware. Changing anything here stops the nightly sync from updating this patient.';
  }

  if (info.source === 'manual') {
    const who = info.updatedBy ?? 'a staff member';
    return on ? `Set manually by ${who} on ${on}.` : `Set manually by ${who}.`;
  }

  return null;
}

/**
 * How long ago the last pack went out, in the coarsest honest unit.
 *
 * Whole weeks past a fortnight, because "17 days ago" reads as precision the
 * value does not have — the pack date is a calendar day, not a timestamp.
 */
function formatElapsed(lastSent: string, today: string): string {
  const days = differenceInCalendarDays(
    parseISO(`${today}T00:00:00`),
    parseISO(`${lastSent}T00:00:00`),
  );
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  return `${Math.floor(days / 7)} weeks ago`;
}

/**
 * The always-visible Patient 360 header line for a blister patient.
 *
 * Never computes a next-pack date it was not given: an enrolled patient with no
 * date on file reads "Next pack date not set" rather than a plausible guess
 * staff would act on.
 */
export function formatBlisterHeaderValue(info: {
  nextPackDate: string | null;
  lastPackSentDate: string | null;
  cycleDays: number | null;
  today: string;
}): string {
  if (!info.nextPackDate) return 'Next pack date not set';

  const lastPackClause = info.lastPackSentDate
    ? `Last pack sent ${formatDayMonthYear(info.lastPackSentDate)} (${formatElapsed(info.lastPackSentDate, info.today)})`
    : 'No pack sent yet';

  return [
    lastPackClause,
    `Next pack due ${formatDayMonthYear(info.nextPackDate)}`,
    formatBlisterCycle(info.cycleDays),
  ].join(' · ');
}
